import { useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Box, Chip, Stack, Typography, useTheme, IconButton } from "@mui/material";
import { IconHeader } from "../../../../Icon/IconHader/IconHeader";
import Comments from "../../Information/Comments";
import Icon from "../../../../Icon/Icon";
import Profile from "../../../../../assets/images/profile1.png";
import { ChipInfo } from "../../../../MainComponents/ChipInfo/ChipInfo";
import { Evaluation, Scenarios, Involved, ActivityPlan, Resources, Interviews, Evidences, Conclusion } from "../";
import { GeneralData } from "./GeneralData";
import { ProcessTaskList } from "./ProcessTaskList";
import { InvestigationButton } from "../components";
import { patchInvestigation } from "../../../../../apis";
import EGDatePicker from "../../../../MainComponents/DatePicker/EGDatePicker";

// Summary of the investigation's components
export const Folder = ({
  catalogCompanyRelationship,
  grievanceClassificationCat,
  reportDetail,
  investigation,
  setInvestigation,
  assignedUsers = [],
  handleSelectedSection,
  openModalConfirm,
  closeModalConfirm,
}) => {

  // ---> Custom Hooks <---//
  const [t] = useTranslation("investigation");
  const [t2] = useTranslation("information");
  const theme = useTheme();

  // ---> State for date editing <---//
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [tempDate, setTempDate] = useState(null);

  const handleEditDate = () => {
    setTempDate(investigation?.created_at ? dayjs(investigation.created_at) : null);
    setIsEditingDate(true);
  };

  const handleCancelEditDate = () => {
    setIsEditingDate(false);
    setTempDate(null);
  };

  const handleSaveDate = async () => {
    if (!tempDate || !dayjs(tempDate).isValid()) {
      handleCancelEditDate();
      return;
    }

    try {
      await patchInvestigation(investigation.alias, { created_at: dayjs(tempDate).toDate() });
      setInvestigation(prev => ({ ...prev, created_at: dayjs(tempDate).toDate() }));
      setIsEditingDate(false);
      setTempDate(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveFolder = async (patch) => {

    const { folder, ...localUpdate } = patch;

    try {
      await patchInvestigation(investigation.alias, patch);
      setInvestigation(prev => ({ ...prev, ...localUpdate, dates: { ...(prev.dates ?? {}), ...folder.dates } }));
    } catch (error) {
      console.error(error);
    }
  };

  const openModalReopenInvestigation = () => {
    openModalConfirm({
      title: t("General.reopen_investigation"),
      titleColor: theme.palette.green.main,
      variant: "success",
      ethiquin: "investigation",
      description1: <>
        <Typography mt={2}>
          {t("General.reopen_investigation_subtitle")}
        </Typography>
        <Typography mt={2}>
          {t("General.reopen_investigation_subtitle_2")}
        </Typography>
        <Typography mt={2}>
          <span style={{ color: theme.palette.red.main, fontWeight: 600 }}>
            {t("General.important")}:
          </span> &nbsp;
          {t("General.important_description")}
        </Typography>
      </>,
      question: t("General.reopen_investigation_confirm"),
      onClickRight: handleReopenInvestigation,
      leftButtonText: t("General.cancel"),
      rightButtonText: t("General.confirm"),
    });
  }

  const handleReopenInvestigation = async() => {
    const conclusion = {
      conclusion: investigation.conclusion ?? "",
      comment: investigation.comment ?? "",
      conclusion_at: null,
      folder: {
        dates: {
          ...(investigation.dates ?? {}),
          reopen_at: dayjs().toDate(),
        }
      }
    }
    try {
      await handleSaveFolder(conclusion);
      closeModalConfirm();
    } catch (error) {
      console.error(error);
    }
  }

  const severityOpts = {
    "type::None": t("General.none"),
    "type::null": t("General.none"),
    "type::low": t("General.low"),
    "type::medium": t("General.medium"),
    "type::hight": t("General.high"),
    "type::other": t("General.other"),
  }

  const priorityOpts = {
    "null": t("General.null_priority"),
    "undefined": t("General.undefined_priority"),
    low: t("General.low"),
    medium: t("General.medium"),
    hight: t("General.high"),
    rejected: t("General.rejected"),
  }

  return (
    <Box className="dyTheme1" padding={2}>

      {/* Estatus de la investigacion */}

      

      <Stack direction="row" justifyContent="space-between" >
        {/* Titulo del reporte */}
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
          <img src={Profile} height={"24px"} style={{ marginRight: 10 }} />
          <Typography component="span">
            {t("General.report_investigation")} &nbsp;
          </Typography>
          {investigation?.assigns_details?.length > 0 ? (
            investigation.assigns_details.map((report, index) => (
              <Typography key={report.id} component="span" fontWeight={600}>
                {report.folio}{index < investigation.assigns_details.length - 1 ? ", " : ""}
              </Typography>
            ))
          ) : (
            <Typography component="span" fontWeight={600}>
              {reportDetail?.folio}
            </Typography>
          )}
        </Box>

        {/* Chips */}
        <Stack direction="row" spacing={.5}>
          <ChipInfo
            label={<span>{t("General.severity")}: <b>{severityOpts[reportDetail?.severity?.path] ?? severityOpts["type::None"]}</b></span>}
            icon={
              <div style={{ display: 'grid', placeContent: 'center'  }}>
                <Icon name='bold_flash_2' size={16} />
              </div>
            }
            color="yellow"
            maxWidth={200}
            showAllways={false}
          />
          {
            !Array.isArray(reportDetail?.locked) && Object.keys(reportDetail?.locked).length > 0 &&
            <ChipInfo
              label={<b>{t("General.bloqued")}</b>}
              icon={
                <div style={{ display: 'grid', placeContent: 'center'  }}>
                  <Icon name='bold_lock_slash' size={16} />
                </div>
              }
              color="red"
              maxWidth={200}
              showAllways={false}
            />
          }
          {
            !Array.isArray(reportDetail?.compromised_anonymity) && Object.keys(reportDetail?.compromised_anonymity).length > 0 &&
            <ChipInfo
              label={<b>{t("General.compromised_anonymity")}</b>}
              icon={
                <div style={{ display: 'grid', placeContent: 'center'  }}>
                  <Icon name='bold_user_remove' size={16} />
                </div>
              }
              color="teal"
              maxWidth={200}
              showAllways={false}
            />
          }
          {
            !Array.isArray(reportDetail?.hostile_place) && Object.keys(reportDetail?.hostile_place).length > 0 &&
            <ChipInfo
              label={<b>{t("General.hostile_place")}</b>}
              icon={
                <div style={{ display: 'grid', placeContent: 'center'  }}>
                  <Icon name='bold_radar' size={16} />
                </div>
              }
              color="purple"
              maxWidth={200}
              showAllways={false}
            />
          }
          <Chip
            label={<b>{t("General.priority")} {priorityOpts[investigation?.evaluation?.priority] ?? t("General.without_assign")}</b>}
            color="orange" 
            variant="outlined" 
            sx={{ height: 24, fontSize: 12 }}
          />
        </Stack>
      </Stack>
      
      <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" mt={2} >
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {/* Inicio de la investigación */}
          <Stack direction="row" display="flex" flex={"1 0 285px"} alignItems="center" spacing={.5}>
            <Icon name="calendar_bold" size={24} color={theme.palette.aquaSky.main} />
            <Typography component="span" color="secondary.main">
              {t("General.investigation_start")}:
            </Typography>
            {!isEditingDate ? (
              <>
                <Typography component="span" fontWeight={700} color={theme.palette.aquaSky.main}>
                  {dayjs(investigation?.created_at).isValid()
                    ? dayjs(investigation?.created_at).format("DD/MMM/YYYY")
                    : t("General.no_information")}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleEditDate}
                  sx={{ padding: 0.5 }}
                >
                  <Icon name="calendar_edit" size={20} color={theme.palette.secondary.main} />
                </IconButton>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ minWidth: 180 }}>
                  <EGDatePicker
                    label=""
                    value={tempDate}
                    onChange={(newDate) => setTempDate(newDate)}
                    format="DD/MMM/YYYY"
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={handleSaveDate}
                  sx={{ padding: 0.5 }}
                >
                  <Icon name="bold_tick_circle" size={20} color={theme.palette.green.main} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCancelEditDate}
                  sx={{ padding: 0.5 }}
                >
                  <Icon name="bold_close_circle" size={20} color={theme.palette.red.main} />
                </IconButton>
              </Box>
            )}
          </Stack>
          {/* Investigación reabierta */}
          {
            investigation?.dates?.reopen_at && (
              <Stack direction="row" display="flex" flex={"1 0 285px"} alignItems="center" spacing={.5}>
                <Icon name="calendar_bold" size={24} color={theme.palette.green.main} />
                <Typography component="span" color="secondary.main">
                  {t("General.investigation_reopened")}:
                </Typography>
                <Typography component="span" fontWeight={700} color={theme.palette.green.main}>
                  {dayjs(investigation?.dates?.reopen_at).isValid() 
                    ? dayjs(investigation?.dates?.reopen_at).format("DD/MMM/YYYY") 
                    : t("General.no_information")}
                </Typography>
              </Stack>
            )
          }
          
          {/* Fecha de conclusión */}
          <Stack direction="row" display="flex" flex={"1 0 285px"} alignItems="center" spacing={.5}>
            <Icon name="calendar_bold" color={theme.palette.red.main} size={24} />
            <Typography component="span" color="secondary.main">
              {t("General.conclusion_date_label")}:
            </Typography>
            <Typography component="span" fontWeight={700} color={theme.palette.red.main}>
              {dayjs(investigation?.conclusion_at).isValid() 
                ? dayjs(investigation?.conclusion_at).format("DD/MMM/YYYY") 
                : t("General.no_information")}
            </Typography>
          </Stack>
        </Stack>
        <Box maxWidth={"200px"} flexShrink={1} mt={{ xs: 2, lg: 0 }}>
          {
            investigation.conclusion && investigation.conclusion_at ? (
              <InvestigationButton 
                label={t("General.reopen_investigation_btn")}
                variant="success"
                onClick={openModalReopenInvestigation}
                wordBreak="break-word"
                fullWidth
              />
            ) : (
              <InvestigationButton 
                label={t("General.conclude_investigation_btn")}
                variant="danger"
                // TODO: si la investigación tiene conclusiones debe salir el modal para concluir, si no debe mandar a la sección de conclusiones
                onClick={() => handleSelectedSection("conclusion")}
                wordBreak="break-word"
                fullWidth
                disabled={investigation.conclusions?.length === 0}
              />
            )
          }
        </Box>
      </Stack>

      {/* Task List */}
      <ProcessTaskList reportDetail={reportDetail} />

      {/* Usuarios asignados */}
      {assignedUsers.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={1} mt={2}>
          <Icon name="bold_user_tick" size={20} color={theme.palette.primary.main} />
          <Typography component="span" color="secondary.main" fontWeight={600}>
            {t("General.assigned_users")}:
          </Typography>
          <Typography component="span" color="secondary.main">
            {assignedUsers.map((user) => user.name).join(", ")}
          </Typography>
        </Stack>
      )}

      {/* Informacion del reporte */}
      <Box
        className="investigation__header rounded dyTheme2 pt-[10] pe-[20] pb-[16] ps-[20]"
        mt={1.7}
        sx={{ border: `1px solid ${theme.palette.primary.main}` }}
        marginBottom={2}
      >
        <Typography
          variant="h5"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          {reportDetail?.subject || t2("information.no_subject")}
        </Typography>

        <Typography variant="h6" color="primary" className="mt-[15]">
          {`${t2("information.report_description")}:`}
        </Typography>

        <Comments
          padding={0}
          maxLength={500}
          text={reportDetail?.description || t2("information.no_description")}
          color="secondary"
        />

      </Box>

      <GeneralData
        reportDetail={reportDetail}
        investigation={investigation}
        catalogCompanyRelationship={catalogCompanyRelationship}
        grievanceClassificationCat={grievanceClassificationCat}
      />

      

      <Stack spacing={2}>

        <Evaluation investigation={investigation} handleSelectedSection={handleSelectedSection} justView />

        <Scenarios reportDetail={reportDetail} investigation={investigation} handleSelectedSection={handleSelectedSection} justView />

        <Involved reportDetail={reportDetail} investigation={investigation}  handleSelectedSection={handleSelectedSection} justView />

        <ActivityPlan investigation={investigation} handleSelectedSection={handleSelectedSection} justView />

        <Resources reportDetail={reportDetail} investigation={investigation}  handleSelectedSection={handleSelectedSection} justView />

        <Interviews reportDetail={reportDetail} investigation={investigation} handleSelectedSection={handleSelectedSection} justView />

        <Evidences investigation={investigation} handleSelectedSection={handleSelectedSection} justView />

        <Conclusion investigation={investigation} handleSelectedSection={handleSelectedSection} justView />
        
      </Stack>

    </Box>
  );
};
